/**
 * Canonical shape stored in router state and sent as `formData` on POST /service-wizard-request-api.
 * Human-readable Q&A and `_fieldIds` for option ids live under `additionalDetails`.
 *
 * @typedef {Object} ServiceWizardFormData
 * @property {string|null} urgency
 * @property {string[]} photos Public blob URLs for uploaded request photos.
 * @property {string} details Free-text notes from the wizard.
 * @property {string} city
 * @property {string} state
 * @property {Record<string, string|object>} additionalDetails Wizard question (title) → answer label, plus `_fieldIds` for internal ids.
 */

/** Machine-readable option ids for routing / resume (not for provider email). */
export const WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY = '_fieldIds';

const TOP_LEVEL_KEYS = new Set([
  'urgency',
  'photos',
  'details',
  'city',
  'state',
  'additionalDetails',
]);

/**
 * Normalize API `photos` to URL strings (ignores legacy `{ name }` entries).
 * @param {unknown} photos
 * @returns {string[]}
 */
export function normalizeWizardPhotoUrls(photos) {
  if (!Array.isArray(photos)) return [];
  return photos
    .map((p) => {
      if (typeof p === 'string') return p.trim();
      return '';
    })
    .filter(Boolean);
}

/**
 * Collect display/upload URLs from flat wizard photo entries.
 * @param {unknown} photos
 * @returns {string[]}
 */
function photoNameFromUrl(url) {
  if (!url?.trim()) return '';
  try {
    const segment = new URL(url).pathname.split('/').pop() || '';
    const decoded = decodeURIComponent(segment);
    return decoded.trim();
  } catch {
    return '';
  }
}

/**
 * @param {unknown} photos
 * @returns {Array<{ src: string, name: string }>}
 */
export function collectPhotoDisplayItemsFromFlat(photos) {
  if (!Array.isArray(photos)) return [];
  return photos
    .map((p, i) => {
      let src = '';
      let name = '';
      if (typeof p === 'string') {
        src = p.trim();
        name = photoNameFromUrl(src) || `Photo ${i + 1}`;
      } else if (p && typeof p === 'object') {
        if (typeof p.name === 'string' && p.name.trim()) {
          name = p.name.trim();
        } else if (p.file?.name) {
          name = String(p.file.name).trim();
        }
        if (typeof p.url === 'string' && p.url.trim()) {
          src = p.url.trim();
        } else if (typeof p.preview === 'string' && p.preview.trim()) {
          src = p.preview.trim();
        }
        if (!name) {
          name = photoNameFromUrl(src) || `Photo ${i + 1}`;
        }
      }
      if (!src) return null;
      return { src, name };
    })
    .filter(Boolean);
}

export function collectPhotoUrlsFromFlat(photos) {
  return collectPhotoDisplayItemsFromFlat(photos).map((item) => item.src);
}

/**
 * Strict check: only the known top-level keys; correct types.
 * @param {unknown} obj
 * @returns {obj is ServiceWizardFormData}
 */
export function isServiceWizardFormData(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  for (const k of Object.keys(obj)) {
    if (!TOP_LEVEL_KEYS.has(k)) return false;
  }
  for (const req of TOP_LEVEL_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(obj, req)) return false;
  }
  if (obj.urgency !== null && typeof obj.urgency !== 'string') return false;
  if (!Array.isArray(obj.photos)) return false;
  for (const photo of obj.photos) {
    if (typeof photo !== 'string' || !photo.trim()) return false;
  }
  if (typeof obj.details !== 'string') return false;
  if (typeof obj.city !== 'string' || typeof obj.state !== 'string') return false;
  const ad = obj.additionalDetails;
  if (ad === null || typeof ad !== 'object' || Array.isArray(ad)) return false;
  return true;
}

/** In-wizard React state (flat): branch keys + `address`. API/router canonical uses top-level city/state. */
export function isFlatWizardFormData(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const addr = obj.address;
  return Boolean(addr && typeof addr === 'object' && !Array.isArray(addr));
}

/** Coerce API payloads that may still have legacy photo shapes into canonical form. */
export function coerceServiceWizardFormData(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  if (isFlatWizardFormData(obj)) {
    return null;
  }
  const photos = normalizeWizardPhotoUrls(obj.photos);
  const coerced = {
    urgency: obj.urgency ?? null,
    photos,
    details: typeof obj.details === 'string' ? obj.details : '',
    city: typeof obj.city === 'string' ? obj.city : '',
    state: typeof obj.state === 'string' ? obj.state : 'KY',
    additionalDetails:
      obj.additionalDetails && typeof obj.additionalDetails === 'object' && !Array.isArray(obj.additionalDetails)
        ? obj.additionalDetails
        : {},
  };
  return isServiceWizardFormData(coerced) ? coerced : null;
}

/** Empty answer skeleton for a trade flow config (problem, branch keys, tail fields). */
function emptyFlatFromFlow(flowConfig) {
  const branchKeys = new Set(['problem']);
  Object.values(flowConfig.flowBranches || {}).forEach((steps) => {
    steps.forEach((s) => branchKeys.add(s.key));
  });
  const initial = {
    problem: null,
    urgency: null,
    photos: [],
    details: '',
    address: { street: '', city: '', state: 'KY', zip: '' },
  };
  branchKeys.forEach((key) => {
    if (key !== 'problem') initial[key] = null;
  });
  return initial;
}

/**
 * Restore flat wizard state from canonical (resume / review).
 * @param {object} flowConfig
 * @param {ServiceWizardFormData} canonical
 */
export function canonicalWizardFormDataToFlat(flowConfig, canonical) {
  const flat = emptyFlatFromFlow(flowConfig);
  if (!isServiceWizardFormData(canonical)) return flat;

  const add = canonical.additionalDetails || {};
  const fieldIds = add[WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY];
  if (fieldIds && typeof fieldIds === 'object' && !Array.isArray(fieldIds)) {
    if (typeof fieldIds.problem === 'string') {
      flat.problem = fieldIds.problem;
    }
    Object.entries(fieldIds).forEach(([key, val]) => {
      if (key === 'problem' || key === WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY) return;
      if (Object.prototype.hasOwnProperty.call(flat, key) && typeof val === 'string') {
        flat[key] = val;
      }
    });
  }

  flat.urgency = canonical.urgency ?? null;
  flat.details = canonical.details;
  flat.photos = canonical.photos.map((url) => ({
    name: photoNameFromUrl(url) || 'photo',
    preview: url,
    file: null,
    url,
  }));
  flat.address = {
    ...flat.address,
    city: canonical.city,
    state: canonical.state.trim() ? canonical.state : 'KY',
  };
  return flat;
}

/**
 * Flat view-model for step sequence, review UI, and summaries.
 * - Canonical payloads (router/API) are converted via {@link canonicalWizardFormDataToFlat}.
 * - Live in-wizard React state is already flat (`address`, `problem`, branch keys); pass through.
 * @param {object} flowConfig
 * @param {unknown} formData
 */
export function wizardFormDataAsFlat(flowConfig, formData) {
  if (!formData || typeof formData !== 'object') return null;
  if (isServiceWizardFormData(formData)) {
    return canonicalWizardFormDataToFlat(flowConfig, formData);
  }
  if (isFlatWizardFormData(formData)) {
    return formData;
  }
  const coerced = coerceServiceWizardFormData(formData);
  if (coerced) {
    return canonicalWizardFormDataToFlat(flowConfig, coerced);
  }
  return null;
}
